import {
  BulkUpdateContainerVisitCommand,
  BulkUpdateContainerVisitNotesCommand,
  ContainerCharacteristics,
  ContainerDamageUpsertDto,
  ContainersApi,
  ContainerType,
  CreateContainerDamageCommand,
  CreateContainerVisitCommand,
  CreateOverlandedContainerVisitCommand,
  ResolveContainerDamagesCommand,
  UnitType,
  UnresolveContainerDamagesCommand,
  UpdateContainerVisitCommand,
  UploadContainerDamageImagesResponseDto,
  UpsertContainerCommand,
} from '@planning/app/api'
import { createApiClient } from '@planning/app/http-client'
import { IContainerDamage } from '@planning/components'

class ContainerService {
  httpClient = createApiClient(ContainersApi)

  async get(page: number, pageSize: number, filter?: string, unitType?: UnitType) {
    const { data } = await this.httpClient.get(page, pageSize, unitType, undefined, filter)

    return data
  }

  async getByIds(ids: number[]) {
    const { data } = await this.httpClient.getByIds({ ids })

    return data
  }

  async post(cmd: UpsertContainerCommand) {
    const { data: id } = await this.httpClient.post(cmd)

    return id
  }

  async getContainersByPartialNumber(
    searchFilter: string,
    isEmpty?: boolean,
    hasServiceOrders?: boolean,
    canAssignServiceOrders?: boolean,
    type?: ContainerType,
    isoCode?: string,
    isOnTerminal?: boolean,
    operator?: string,
    includeRestow?: boolean,
  ) {
    const { data } = await this.httpClient.getContainersByPartialNumber(
      searchFilter,
      isEmpty,
      isOnTerminal,
      isoCode,
      operator,
      hasServiceOrders,
      canAssignServiceOrders,
      type,
      includeRestow,
    )

    return data
  }

  async createDamage(cmd: CreateContainerDamageCommand) {
    await this.httpClient.damage(cmd.containerId, cmd)
  }

  async resolveDamages(cmd: ResolveContainerDamagesCommand) {
    await this.httpClient.resolveDamages(cmd.containerId, cmd)
  }

  async unresolveDamages(cmd: UnresolveContainerDamagesCommand) {
    await this.httpClient.unresolveDamages(cmd.containerId, cmd)
  }

  async createContainerVisit(id: number, cmd: CreateContainerVisitCommand) {
    const { data } = await this.httpClient.createContainerVisit(id, cmd)
    return data
  }
  async createOverlandedContainerVisit(cmd: CreateOverlandedContainerVisitCommand) {
    const { data } = await this.httpClient.createOverlandedContainerVisit(cmd)
    return data as any
  }

  async updateContainerVisit(cmd: UpdateContainerVisitCommand) {
    await this.httpClient.updateContainerVisit(cmd)
  }

  async bulkUpdateContainerVisitNotes(cmd: BulkUpdateContainerVisitNotesCommand) {
    await this.httpClient.bulkUpdateContainerVisitNotes(cmd)
  }

  async linkableOrders(containerNumbers: string[]) {
    const { data } = await this.httpClient.linkableOrders({ numbers: containerNumbers })
    return data
  }

  async bulkEdit(cmd: BulkUpdateContainerVisitCommand) {
    await this.httpClient.bulkUpdateContainerVisit(cmd)
  }

  async uploadDamageImagesForContainer(
    containerNumber: string,
    images: File[],
  ): Promise<UploadContainerDamageImagesResponseDto[]> {
    if (!images.length) {
      return []
    }

    const { data } = await this.httpClient.containerDamageImages(containerNumber, images)

    return data
  }

  async getDamageDtoForUploadedDamages(containerNumber: string, damages: IContainerDamage[]) {
    const requests = damages.map(x =>
      this.uploadDamageImagesForContainer(containerNumber, x.images),
    )

    const imagePaths = await Promise.all(requests)

    return damages.map(
      (x, index) =>
        ({
          description: x.description,
          size: x.size,
          typeIds: x.containerDamageTypes,
          locationId: x.containerDamageLocation,
          imagePaths: imagePaths[index].map(img => img.imagePath) ?? [],
        }) as ContainerDamageUpsertDto,
    )
  }

  async getContainerCharacteristicsByIsoCode(
    isoCode: string,
    onlyStandard: boolean,
  ): Promise<ContainerCharacteristics> {
    const { data } = await this.httpClient.getContainerCharacteristicsByIsoCode(
      isoCode,
      onlyStandard,
    )
    return data
  }

  // return list of invalid iso codes
  async validateIsoCodes(isoCodes: string[]) {
    const { data } = await this.httpClient.validateIsoCodeList(isoCodes)
    return data
  }

  async getAttributes(containerNumber: string) {
    const { data } = await this.httpClient.getContainerAttributes(containerNumber)
    return data
  }

  async getContainerHistoryAdditionalData(userIds?: string[], carrierVisitIds?: number[]) {
    const { data } = await this.httpClient.getContainerHistoryAdditionalData(
      userIds,
      carrierVisitIds,
    )

    return data
  }

  async getContainerInfoForTallyQuery(containerNumber: string) {
    const { data } = await this.httpClient.getContainerInfoForTallyQuery(containerNumber)

    return data
  }
}

const containerService = new ContainerService()

export default containerService
